import Bugsnag from '@bugsnag/js'
import {
  postRequestStartupPositionApplicationTempApply,
  postStartupPositionApplicationTemp,
  postStartupPositionApplicationTempFulfillments,
  putStartupPositionApplicationTemp,
} from 'apis/request'
import { analytics } from 'infra/analytics'
import { makeAutoObservable } from 'mobx'
import TagManager from 'react-gtm-module'
import toast from 'react-hot-toast'
import { mutate } from 'swr'
import {
  StartupDetail,
  ApplicationFulfillment,
  StartupPositionDetail,
  TempApplication,
  ApplicationFulfillmentParams,
  WithIdLink,
  ApplicationParams,
} from 'types/common'

export class PositionApplicationStore {
  // temp data
  files: WithIdLink[]
  links: string[]
  joinReason: string
  fitReason: string
  fulfillments: ApplicationFulfillment
  //---
  startupDetail: StartupDetail | null
  positionDetail: StartupPositionDetail | null
  curStep: number
  curTab: number
  isOpenPreviewModal: boolean
  isOpenDoneModal: boolean
  isOpenMessageCheckModal: boolean
  hasCreatedTemp: boolean
  isSavingJoinReason: boolean
  isSavingFitReason: boolean
  isSavingFulfillment: boolean
  selectedDraftFulfillments: ApplicationFulfillmentParams[]
  isSelectedJoinReasonDraftTab: boolean
  isSelectedFitReasonDraftTab: boolean
  isSelectedFulfillmentDraftTab: boolean

  constructor() {
    this.files = []
    this.links = ['']
    this.joinReason = ''
    this.fitReason = ''
    this.fulfillments = {
      task: [],
      preferred: [],
      qualification: [],
    }
    this.startupDetail = null
    this.positionDetail = null
    this.curStep = 0
    this.curTab = 0
    this.isOpenPreviewModal = false
    this.isOpenDoneModal = false
    this.isOpenMessageCheckModal = false
    this.hasCreatedTemp = false
    this.isSavingJoinReason = false
    this.isSavingFitReason = false
    this.isSavingFulfillment = false
    this.selectedDraftFulfillments = []
    this.isSelectedJoinReasonDraftTab = false
    this.isSelectedFitReasonDraftTab = false
    this.isSelectedFulfillmentDraftTab = false
    makeAutoObservable(this)
  }

  get fileIds() {
    return this.files.map((file) => file.id)
  }

  get validLinks() {
    return this.links.filter((link) => link !== '')
  }

  get hasAttachment() {
    return this.files.length > 0 || this.links.some((link) => link !== '')
  }

  get selectedDraftFulfillmentIds() {
    return this.selectedDraftFulfillments.map((item) => item.conditionId)
  }

  get applicationAlert() {
    return `${this.startupDetail?.name} ‧ ${this.positionDetail?.name}에 지원하시겠어요?`
  }

  setState<K extends keyof this>(key: K, value: this[K]) {
    this[key] = value
  }

  movePrevStep() {
    this.curStep = this.curStep - 1
  }

  moveNextStep() {
    this.curStep = this.curStep + 1
  }

  async mutateTemp() {
    return mutate(
      `/startup-positions/${this.positionDetail?.id}/applications/temp`,
    )
  }

  async mutateAndUpdateTemp() {
    const { data } = await this.mutateTemp()
    this.setTemp(data)
  }

  setLink(link: string, index: number) {
    this.links[index] = link
    this.links = this.links.filter((link) => link !== '')
    if (this.links.at(-1) !== '') this.links = [...this.links, '']
  }

  addFile(file: WithIdLink) {
    this.files = [...this.files, file]
  }

  removeFile(fileId: number) {
    this.files = this.files.filter((item) => item.id !== fileId)
  }

  setFulfillment(
    type: 'preferred' | 'task' | 'qualification',
    index: number,
    params: { isFit?: boolean; description?: string },
  ) {
    this.fulfillments[type][index] = {
      ...this.fulfillments[type][index],
      ...params,
    }
  }

  async saveTempWithErrorHandling(params: ApplicationParams) {
    putStartupPositionApplicationTemp(
      this.positionDetail?.id || 0,
      false,
      params,
    ).catch(() =>
      alert('일시적인 오류가 발생했습니다.\n새로고침하여 다시 시도해주세요.'),
    )
  }

  async apply(isPossibleApply: boolean) {
    analytics.track('click_positions_position_apply_submit')
    if (!isPossibleApply) {
      Bugsnag.notify('지원 모달인데 지원 불가') // 발생하면 안 되는데 혹시나
      throw '지원 가능 횟수를 초과하여 해당 공고에 지원할 수 없어요.'
    }
    const positionId = this.positionDetail?.id || 0
    const id = toast.loading('지원중')
    await postRequestStartupPositionApplicationTempApply(positionId)
    toast.remove(id)
    TagManager.dataLayer({
      dataLayer: {
        event: 'done_apply',
        startup: this.startupDetail?.name,
      },
    })
  }

  async initTemp(temp: TempApplication | null | undefined, positionId: number) {
    if (this.hasCreatedTemp || temp === undefined) return
    this.setState('hasCreatedTemp', true)
    if (temp === null) {
      await this.createAndSetTemp(positionId)
    } else if (temp.fulfillments === null) {
      await this.updateFulfillmentsAndSetTemp(positionId)
    } else {
      this.setTemp(temp)
    }
  }

  async createAndSetTemp(positionId: number) {
    // 새로운 TempApplication을 생성하고 설정
    await postStartupPositionApplicationTemp(positionId)
    await this.mutateAndUpdateTemp()
  }

  async updateFulfillmentsAndSetTemp(positionId: number) {
    // fulfillments를 업데이트하고 Temp를 설정
    await postStartupPositionApplicationTempFulfillments(positionId)
    await this.mutateAndUpdateTemp()
  }

  setTemp(temp: TempApplication) {
    this.setState('links', [...temp.links.map((x) => x.link), ''])
    this.setState('files', temp.files)
    this.setState('fitReason', temp.fitReason || '')
    this.setState('joinReason', temp.coverLetter || temp.joinReason || '')
    temp.fulfillments && this.setState('fulfillments', temp.fulfillments)
  }

  toggleSelectedDraftFulfillment(item: ApplicationFulfillmentParams) {
    if (this.selectedDraftFulfillmentIds.includes(item.conditionId)) {
      this.selectedDraftFulfillments = this.selectedDraftFulfillments.filter(
        (i) => i.conditionId !== item.conditionId,
      )
    } else {
      this.selectedDraftFulfillments = [...this.selectedDraftFulfillments, item]
    }
  }
}
